// Phase DELHIVERY-1 (HYBRID) — admin/MHM courier shipments list page.
import { useEffect, useState } from "react";
import { Truck, RefreshCw, ExternalLink } from "lucide-react";
import api from "../../services/api";
import { useOrderDrawer } from "../../components/hub/OrderDrawerContext";

const STATUS_BADGE = {
  received_by_courier: "bg-amber-100 text-amber-800 border-amber-200",
  in_transit:          "bg-violet-100 text-violet-800 border-violet-200",
  out_for_delivery:    "bg-sky-100 text-sky-800 border-sky-200",
  delivered:           "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled:           "bg-slate-100 text-slate-600 border-slate-200",
  rto:                 "bg-rose-100 text-rose-800 border-rose-200",
  exception:           "bg-rose-100 text-rose-800 border-rose-200",
};

export default function CourierShipmentsList({ scope = "admin" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const { open } = useOrderDrawer();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (partnerFilter) params.set("courier_partner", partnerFilter);
      params.set("limit", "100");
      const r = await api.get(`/api/courier/shipments?${params.toString()}`);
      setRows(r.data?.shipments || []);
    } catch (e) {
      console.error("courier shipments load failed", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, partnerFilter]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`courier-shipments-list-${scope}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-slate-800">
          <Truck className="h-5 w-5 text-orange-500" />
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight">Courier Shipments</h1>
          {scope === "hub" && (
            <span className="text-xs text-slate-500">(your hub only)</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="courier-shipments-filter-status"
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
            <option value="">All statuses</option>
            <option value="received_by_courier">Received by courier</option>
            <option value="in_transit">In transit</option>
            <option value="out_for_delivery">Out for delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="rto">RTO</option>
          </select>
          <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}
                  data-testid="courier-shipments-filter-partner"
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
            <option value="">All partners</option>
            <option value="delhivery">Delhivery</option>
            <option value="ekart">Ekart</option>
            <option value="dtdc">DTDC</option>
            <option value="xpressbees">Xpressbees</option>
          </select>
          <button onClick={load} data-testid="courier-shipments-refresh"
                  className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left py-2.5 px-4 font-medium">Tracking ID</th>
              <th className="text-left py-2.5 px-4 font-medium">AWB</th>
              <th className="text-left py-2.5 px-4 font-medium">Partner</th>
              <th className="text-left py-2.5 px-4 font-medium">Status</th>
              <th className="text-left py-2.5 px-4 font-medium">Last synced</th>
              <th className="text-right py-2.5 px-4 font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400"
                      data-testid="courier-shipments-empty">No shipments match the filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.tracking_id} className="hover:bg-orange-50/40"
                  data-testid={`courier-shipments-row-${r.tracking_id}`}>
                <td className="px-4 py-2.5 font-mono text-slate-800">{r.tracking_id}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600" data-testid={`courier-shipments-awb-${r.tracking_id}`}>
                  {r.courier_awb || "—"}
                </td>
                <td className="px-4 py-2.5 text-slate-600 capitalize">{r.courier_partner || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${
                    STATUS_BADGE[r.courier_status] || "bg-slate-100 text-slate-600 border-slate-200"
                  }`}>
                    {r.courier_status_label || r.courier_status || "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">
                  {r.courier_last_synced_at ? new Date(r.courier_last_synced_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => open(r.tracking_id)}
                          data-testid={`courier-shipments-open-${r.tracking_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-slate-200 hover:bg-orange-50 rounded-lg">
                    <ExternalLink className="h-3 w-3" /> Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
